import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { subscribeToPush, getPushStatus } from "@/lib/push";
import { toast } from "sonner";
import { Bell, X } from "lucide-react";

const VISITS_KEY = "ovolaay_push_visits";
const SNOOZE_KEY = "ovolaay_push_snooze";
const SHOW_AFTER = 3; // visites avant 1er rappel
const SNOOZE_STEP = 3; // visites avant réapparition après "Plus tard"

// Rappel discret et non-intrusif pour activer les notifications,
// affiché aux utilisateurs qui ne les ont pas encore acceptées, après quelques visites.
export function PushReminder() {
  const { user } = useAuth();
  const { t } = useT();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) { setShow(false); return; }
    let cancelled = false;
    (async () => {
      const visits = (parseInt(localStorage.getItem(VISITS_KEY) || "0", 10) || 0) + 1;
      localStorage.setItem(VISITS_KEY, String(visits));
      const snooze = parseInt(localStorage.getItem(SNOOZE_KEY) || "0", 10) || 0;
      const status = await getPushStatus();
      if (cancelled) return;
      // On ne rappelle jamais si déjà activé, refusé, non supporté, ou pas encore assez de visites / snooze en cours.
      const eligible = status.supported && status.permission === "default" && !status.subscribed;
      if (eligible && visits >= SHOW_AFTER && visits >= snooze) setShow(true);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const enable = async () => {
    setBusy(true);
    const ok = await subscribeToPush(true);
    setBusy(false);
    setShow(false);
    if (ok) toast.success(t("push.enabledOk"));
  };

  const later = () => {
    const visits = parseInt(localStorage.getItem(VISITS_KEY) || "0", 10) || 0;
    localStorage.setItem(SNOOZE_KEY, String(visits + SNOOZE_STEP));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="push-reminder"
      className="fixed z-40 left-3 right-3 bottom-24 md:left-auto md:right-6 md:bottom-6 md:w-96 rounded-2xl bg-card border border-border shadow-xl p-4 flex items-start gap-3 animate-in slide-in-from-bottom-4 fade-in duration-300"
    >
      <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <Bell className="h-5 w-5" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-heading font-bold text-secondary text-sm leading-tight">{t("push.remindTitle")}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{t("push.remindDesc")}</p>
        <div className="mt-3 flex gap-2">
          <button onClick={enable} disabled={busy} data-testid="push-reminder-enable"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-xs font-bold text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-60 active:scale-[0.98]">
            <Bell className="h-3.5 w-3.5" />{busy ? t("push.enabling") : t("push.enable")}
          </button>
          <button onClick={later} data-testid="push-reminder-later"
            className="rounded-full px-3.5 py-1.5 text-xs font-bold text-muted-foreground hover:bg-muted transition-colors">
            {t("push.remindLater")}
          </button>
        </div>
      </div>
      <button onClick={later} data-testid="push-reminder-close" aria-label="close"
        className="text-muted-foreground hover:text-secondary transition-colors shrink-0">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
